import { ChevronDownIcon, Cross2Icon, PlusIcon } from "@radix-ui/react-icons";
import { PersonKind } from "client/openapi/models/PersonKind";
import PeoplePicker, {
  TaskPerson,
  TaskRole,
} from "components/Dashboard/ManageTasksDialog/PeoplePicker";
import "components/Dashboard/ManageTasksDialog/create.css";
import { COMPACT_CHECK } from "components/Inputs/compact";
import { useEffect, useId, useRef, useState } from "react";
import { useReference } from "./hooks";
import {
  ASSIGN_CHOICES,
  ASSIGN_GROUPS,
  Facets,
  facetsOf,
  SHARE_MODES,
} from "./kinds";
import { personKey, ReferenceData } from "./reference";
import { resolveChoice, sampleContext } from "./resolve";
import { ChoiceSelect, PillChoice } from "./parts";
import type {
  AssignChoice,
  AssignTo,
  PersonKey,
  PersonRole,
  ShareMode,
  Trigger,
} from "./types";
import { choiceTag } from "./words";

/** A key as the people picker's person, and back. */
function toTaskPerson(ref: ReferenceData, key: PersonKey): TaskPerson | null {
  const person = ref.personByKey[key];
  if (!person) return null;
  const role: TaskRole =
    person.role === "admin" ? "admin" : (person.role as PersonKind);
  return {
    role,
    id: person.role === "admin" ? person.id : Number(person.id),
    label: person.name,
  };
}

function fromTaskPerson(person: TaskPerson): PersonKey {
  return personKey(person.role as PersonRole, person.id);
}

/** Gray example names: "Rosa Diaz", "Rosa Diaz and Sam Doe", "Amy Smith and 2 more". */
function exampleNames(ref: ReferenceData, keys: PersonKey[]) {
  if (!keys.length) return "Nobody in this example";
  const names = keys.map((k) => ref.personByKey[k]?.name ?? "Someone");
  if (names.length <= 2) return names.join(" and ");
  return `${names[0]} and ${names.length - 1} more`;
}

const ANY_SUBJECT = -1;

function SubjectSelect({
  label,
  value,
  onChange,
  anyLabel,
}: {
  label: string;
  value: number | null;
  onChange: (subject: number | null) => void;
  anyLabel: string;
}) {
  const ref = useReference();
  return (
    <ChoiceSelect
      label={label}
      value={value ?? ANY_SUBJECT}
      options={[
        { value: ANY_SUBJECT, label: anyLabel },
        ...ref.subjects.map((s) => ({ value: s.id, label: s.name })),
      ]}
      onChange={(v) => onChange(v === ANY_SUBJECT ? null : v)}
      className="mt-1 min-w-[11rem] max-w-xs"
    />
  );
}

function ChoiceRow({
  choice,
  assign,
  facets,
  example,
  onToggle,
  onChange,
}: {
  choice: AssignChoice;
  assign: AssignTo;
  facets: Facets;
  example: string | null;
  onToggle: () => void;
  onChange: (next: AssignTo) => void;
}) {
  const id = useId();
  const def = ASSIGN_CHOICES[choice];
  const checked = assign.choices.includes(choice);
  const applies = def.applies(facets);
  return (
    <div
      className={`flex items-start gap-2 py-1 ${
        !applies && !checked ? "opacity-50" : ""
      }`}
    >
      <input
        id={id}
        type="checkbox"
        className={`${COMPACT_CHECK} mt-0.5`}
        checked={checked}
        disabled={!applies && !checked}
        onChange={onToggle}
      />
      <div className="min-w-0">
        <label
          htmlFor={id}
          className="block cursor-pointer text-sm text-gray-900"
        >
          {def.label(facets)}
          <span className="block text-xs text-gray-500">
            {applies ? example ?? "" : def.note}
          </span>
        </label>
        {checked && choice === "tutors" && (
          <SubjectSelect
            label="Only their tutors who teach"
            anyLabel="Any subject"
            value={assign.tutorSubject}
            onChange={(tutorSubject) => onChange({ ...assign, tutorSubject })}
          />
        )}
        {checked && choice === "all_tutors" && (
          <SubjectSelect
            label="Only tutors who teach"
            anyLabel="Every tutor"
            value={assign.teachSubject}
            onChange={(teachSubject) => onChange({ ...assign, teachSubject })}
          />
        )}
      </div>
    </div>
  );
}

/**
 * The grouped Assign to choices, inline: related to who it is about, from
 * the event, by role, then specific people and how it is shared. Gray
 * names under each choice show who it means for an example from the
 * organization's own people. If nobody fits, the org's admins get it (the
 * engine says so on the task). onChange gets the whole next value every
 * time.
 */
export function AssignToPanel({
  value,
  onChange,
  trigger,
}: {
  value: AssignTo;
  onChange: (next: AssignTo) => void;
  trigger: Trigger;
}) {
  const ref = useReference();
  const pickerId = useId();
  const facets = facetsOf(ref, trigger);
  const sample = sampleContext(ref, trigger);
  const toggle = (choice: AssignChoice) =>
    onChange({
      ...value,
      choices: value.choices.includes(choice)
        ? value.choices.filter((c) => c !== choice)
        : [...value.choices, choice],
    });
  const people = ref.people
    .map((p) => toTaskPerson(ref, p.key))
    .filter((p): p is TaskPerson => p !== null);
  const chosen = value.people
    .map((k) => toTaskPerson(ref, k))
    .filter((p): p is TaskPerson => p !== null);
  const me = ref.me ? ref.personByKey[ref.me] : null;

  return (
    <div className="text-sm">
      <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        {ASSIGN_GROUPS.map((group) => (
          <div key={group.key}>
            <p className="text-xs font-semibold text-gray-600">{group.label}</p>
            {group.choices.map((choice) => (
              <ChoiceRow
                key={choice}
                choice={choice}
                assign={value}
                facets={facets}
                example={
                  sample
                    ? exampleNames(
                        ref,
                        resolveChoice(ref, sample.ctx, choice, value, facets)
                      )
                    : null
                }
                onToggle={() => toggle(choice)}
                onChange={onChange}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="task-form mt-4 border-t border-gray-100 pt-3">
        <label
          htmlFor={pickerId}
          className="text-xs font-semibold text-gray-600"
        >
          Specific people
        </label>
        <div className="mt-1">
          <PeoplePicker
            inputId={pickerId}
            people={people}
            value={chosen}
            suggested={me ? [toTaskPerson(ref, me.key)!] : []}
            onChange={(next) =>
              onChange({ ...value, people: next.map(fromTaskPerson) })
            }
            placeholder="Find any admin, tutor, parent or student"
          />
        </div>
        {me && !value.people.includes(me.key) && (
          <button
            type="button"
            className="mt-2 inline-flex items-center gap-1 rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-xs font-medium text-gray-700 hover:border-gray-400"
            onClick={() =>
              onChange({ ...value, people: [...value.people, me.key] })
            }
          >
            <PlusIcon /> Me, {me.name}
          </button>
        )}
      </div>

      <div className="mt-4 border-t border-gray-100 pt-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold text-gray-600">
            How it is shared
          </p>
          <PillChoice<ShareMode>
            label="How it is shared"
            value={value.share}
            options={SHARE_MODES}
            onChange={(share) => onChange({ ...value, share })}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * The Assign to field: the chosen groups and people as tags (each with an
 * x), how it is shared at the right, and the choices right under it, in
 * the dialog itself, moving what is below down until it closes. Tadpole
 * people only, never typed emails.
 */
export default function AssignToPicker({
  value,
  onChange,
  trigger,
  label = "Assign to",
}: {
  value: AssignTo;
  onChange: (next: AssignTo) => void;
  trigger: Trigger;
  label?: string;
}) {
  const ref = useReference();
  const [open, setOpen] = useState(false);
  const choicesRef = useRef<HTMLDivElement>(null);
  const choicesId = useId();
  const facets = facetsOf(ref, trigger);
  const share = SHARE_MODES.find((m) => m.value === value.share);
  const tags: { key: string; text: string; remove: () => void }[] = [
    ...value.choices.map((choice) => ({
      key: choice,
      text: choiceTag(ref, choice, value, facets),
      remove: () =>
        onChange({
          ...value,
          choices: value.choices.filter((c) => c !== choice),
        }),
    })),
    ...value.people.map((key) => ({
      key,
      text: ref.personByKey[key]?.name ?? "Someone who left",
      remove: () =>
        onChange({ ...value, people: value.people.filter((p) => p !== key) }),
    })),
  ];

  // Opened, the choices come into view in the scrolling dialog.
  useEffect(() => {
    if (open) choicesRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [open]);

  return (
    <div className="w-full">
      {/* Sized and divided as the dialog's selects. */}
      <div
        className={`flex min-h-[2rem] w-full gap-1 rounded-md border bg-white pl-1 shadow-sm ${
          open
            ? "border-indigo-300 ring ring-indigo-200 ring-opacity-50"
            : "border-gray-300 hover:border-gray-400"
        }`}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 py-[3px]">
          {tags.map((tag) => (
            <span
              key={tag.key}
              className="inline-flex items-center gap-1.5 rounded bg-gray-100 pl-2 pr-1 text-sm leading-6"
            >
              {tag.text}
              <button
                type="button"
                aria-label={`Remove ${tag.text}`}
                className="rounded-md border border-gray-200 bg-white p-0.5 text-gray-500 hover:border-red-300 hover:bg-red-50 hover:text-red-800"
                onClick={tag.remove}
              >
                <Cross2Icon />
              </button>
            </span>
          ))}
          <button
            type="button"
            aria-label={label}
            aria-expanded={open}
            aria-controls={choicesId}
            className="min-w-[8rem] flex-1 self-stretch px-1 text-left text-sm text-gray-400"
            onClick={() => setOpen(!open)}
          >
            {tags.length ? "" : "Add a group or a person"}
          </button>
        </div>
        {tags.length > 0 && share && (
          <span className="flex-none self-center whitespace-nowrap px-1 text-xs text-gray-500">
            {share.label}
          </span>
        )}
        <span className="flex flex-none items-center gap-1 p-1">
          <span className="w-px self-stretch bg-gray-300" />
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="rounded-md p-[3px] text-gray-500 hover:bg-gray-100 hover:text-black"
            onClick={() => setOpen(!open)}
          >
            <ChevronDownIcon />
          </button>
        </span>
      </div>
      {open && (
        <div
          ref={choicesRef}
          id={choicesId}
          role="group"
          aria-label={label}
          className="mt-1 rounded-lg border border-gray-200 bg-white p-3 shadow-sm"
        >
          <AssignToPanel value={value} onChange={onChange} trigger={trigger} />
        </div>
      )}
    </div>
  );
}
